import * as Sentry from '@sentry/react';
import { useRouter } from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { RefreshCw, ShieldX, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';

import { ApiError } from '~/api/errors';
import { Button } from '~/components/ui/button';
import {
  attemptChunkRecovery,
  isChunkLoadError,
  reloadWithFreshDocument,
} from '~/utils/chunk-error';

/**
 * Route-level error boundary for individual route segments.
 * Used as `errorComponent` on layout or page routes.
 * Displays a permission-denied UI for 403s, a "new version" UI for stale-deploy chunk
 * failures, and a generic error UI otherwise.
 */
export function RouteErrorBoundary({ error }: ErrorComponentProps) {
  const router = useRouter();
  const isForbidden = error instanceof ApiError && error.code === 'FORBIDDEN';
  const isChunkError = isChunkLoadError(error);
  const [isReloading, setIsReloading] = useState(false);

  useEffect(() => {
    // Don't report 403s to Sentry — they're expected access denials, not bugs
    if (!isForbidden) {
      // Tag stale-deploy chunk failures so they can be filtered out of real crash triage
      Sentry.captureException(
        error,
        isChunkError ? { tags: { errorType: 'chunk-load' } } : undefined,
      );
    }
  }, [error, isForbidden, isChunkError]);

  // Backstop for the `vite:preloadError` listener in main.tsx: a chunk failure surfaced through
  // a route loader can reach the boundary without that event firing. The guard inside
  // attemptChunkRecovery makes a duplicate call a no-op, so this never doubles a reload.
  // PROD-only for the same reason as the listener — in dev this message can mean a transform
  // error, and reloading would hide it.
  useEffect(() => {
    if (!isChunkError || !import.meta.env.PROD) return;
    void attemptChunkRecovery();
  }, [isChunkError]);

  if (isForbidden) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <ShieldX aria-hidden="true" className="text-muted-foreground mx-auto h-12 w-12" />
          <h2 className="mt-4 text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground mt-2 max-w-sm">
            You don&apos;t have permission to view this page. Contact your administrator if you
            believe this is a mistake.
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Button
              variant="secondary"
              className="cursor-pointer"
              onClick={() => {
                router.history.back();
              }}
            >
              Go back
            </Button>
            <Button
              className="cursor-pointer"
              onClick={() => {
                void router.navigate({ to: '/' });
              }}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // A chunk failure is not a server or connectivity problem — the deploy simply moved on while
  // this tab was open — so it gets its own icon and copy instead of the wifi wording.
  const Icon = isChunkError ? RefreshCw : WifiOff;

  /**
   * Chunk failures need the document itself re-fetched, because a plain reload can be served the
   * same cached `index.html` that pointed at the missing chunk. Every other error keeps the
   * existing plain reload.
   */
  const handleReload = () => {
    if (!isChunkError) {
      window.location.reload();
      return;
    }
    setIsReloading(true);
    void reloadWithFreshDocument();
  };

  return (
    <section role="alert" aria-live="polite" className="flex h-96 items-center justify-center px-4">
      <div className="mt-6 flex max-w-md flex-col items-center text-center">
        <div className="bg-selected-row mb-6 flex h-16 w-16 items-center justify-center rounded-full">
          <Icon aria-hidden="true" className="text-muted-foreground h-6 w-6" />
        </div>

        <h2 className="text-foreground mb-2 text-2xl font-semibold tracking-tight">
          {isChunkError ? 'A new version is available' : "We couldn't load this page"}
        </h2>

        <p className="text-muted-foreground text-md mb-8">
          {isChunkError
            ? 'This app was updated while your tab was open, so part of this page could no longer be loaded. Reloading picks up the new version.'
            : "The page didn't load. Could be our servers, could be your wifi - Most of the time, a quick reload sorts it out."}
        </p>

        <div className="flex items-center justify-center gap-3">
          <Button
            variant="outline"
            className="cursor-pointer"
            onClick={() => {
              router.history.back();
            }}
          >
            Go back
          </Button>
          <Button className="cursor-pointer" isLoading={isReloading} onClick={handleReload}>
            Reload page
          </Button>
        </div>
      </div>
    </section>
  );
}
